import React from 'react';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettings } from 'services/settings/SettingsContext';
import { renderDate, renderDocumentNumber, renderStatusBadge } from 'components/lists/functions/renderListColumn';
import { useTranslation } from 'react-i18next';
import PurchaseModal from './PurchaseModal';
import ListColumnCheckIcon from 'components/lists/columns/ListColumnCheckIcon';

// Set the filters for the purchase list:
export const purchaseListFilters: ListFilterType[] = [
    {
        id: 'active',
        label: 'purchase.general.all_purchases_filter_label',
        params: { 'deleted': false },
        default: true,
    },
    {
        group: 'purchase.general.status_filter_group_label',
        filters: [
            {
                id: 'draft',
                label: 'purchase.status.draft',
                params: { 'status': 'draft' },
            },
            {
                id: 'open',
                label: 'purchase.status.open',
                params: { 'status': 'open' },
            },
            {
                id: 'confirmed',
                label: 'purchase.status.confirmed',
                params: { 'status': 'confirmed' },
            },
            {
                id: 'expired',
                label: 'purchase.status.expired',
                params: { 'status': 'expired' },
            },
            {
                id: 'delivered',
                label: 'purchase.status.delivered',
                params: { 'status': 'delivered' },
            },
        ]
    },
    {
        group: 'general.archive',
        filters: [
            {
                id: 'deleted',
                label: 'purchase.general.deleted_purchases_label',
                shortName: 'general.deleted',
                params: { 'deleted': true },
            }
        ]   
    }
]

// Rename the name of the function:
const PurchaseList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_purchase_list',
        defaultParams: { 'deleted': false },
        object: 'purchase'
    }
    const objectName = 'purchase.general.object_name'
    const showSearch = true
    const showImport = false
    const showExport = false

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const postNewItem = { url: 'post_purchase' }

    const detailModal = <PurchaseModal />;
    const onRowClick = 'detailModal'  

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        { 
            width: '1.5fr', 
            field: 'number', 
            label: 'purchase.general.purchase_number_label', 
            render: (_, row) => renderDocumentNumber(row.number, row.status, 'purchase') 
        },
        { 
            width: '3fr', 
            field: 'supplier_name', 
            orderField: 'supplier__name',
            label: 'purchase.general.supplier_label' 
        },
        {
            width: "3.5fr",
            field: "purchase_info",
            label: "purchase.general.linked_client_label",
            disableOrdering: true,
            render: (_, row) => renderPurchaseInfo(row.order_number, row.job_number, (row.order_client_name ?? row.job_client_name))
        },
        { 
            width: '2fr', 
            field: 'purchase_date', 
            label: 'purchase.general.purchase_date_label', 
            render: (_, row) => renderDate(row.purchase_date, userLocale) 
        },
        { 
            width: '2fr', 
            field: 'expected_delivery_date', 
            label: 'purchase.general.delivery_date_label', 
            render: (_, row) => renderDate(row.expected_delivery_date, userLocale) 
        },
        { 
            width: '144px', 
            field: 'status', 
            label: 'purchase.general.status_label', 
            disableOrdering: true,
            render: (_, row) => renderStatusBadge(row.status) 
        },
        {
            width: '36px',
            field: 'approved',
            label: '',
            render: (_, row) => <ListColumnCheckIcon checked={row.is_checked} tooltipText='purchase.general.purchase_is_confirmed_tooltipText' />,
            disableOrdering: true,
        }
    ];

    // Render the order and/or job number and client name
    const renderPurchaseInfo = (
        order_number: string | null,
        job_number: string | null,
        client_name: string | null
    ) => {
        const numberParts: string[] = [];

        if (order_number) {
            numberParts.push(`${t('purchase.general.order_label')} ${order_number}`);
        }

        if (job_number) {
            numberParts.push(`${t('purchase.general.job_label')} ${job_number}`);
        }

        if (!client_name && numberParts.length === 0) {
            return <span>-</span>;
        }

        if (!client_name) {
            return <span>{numberParts.join(' - ')}</span>;
        }

        if (numberParts.length === 0) {
            return <span>{client_name}</span>;
        }

        return (
            <span>
                {client_name} ({numberParts.join(' - ')})
            </span>
        );
    };

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filters={purchaseListFilters}
            showSearch={showSearch}
            showImport={showImport}
            showExport={showExport}
            postNewItem={postNewItem}
            detailModal={detailModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
        />
    );
};

export default PurchaseList;